import React, { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { Box, Button, Container, Stack, TextField, Typography, Paper } from "@mui/material";
import { useSelector } from "react-redux";
import { PersonalDetails } from "../../assets";
import { editPersonalDetails, getPersonalDetails } from "../../services";
import { notification } from "antd";
import { ErrorText } from "../../components";

const PersonalDetail = () => {
  const theme = useTheme();
  const user = JSON.parse(sessionStorage.getItem("userDetails"));
  
  const userId = user?.id;

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState({
    username: "",
    email: "",
    contactNumber: "",
  });
  const [initialValues, setInitialValues] = useState({
    username: "",
    email: "",
    contactNumber: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getPersonalDetails({ userId });
        const data = res?.data;
        setFormValues({
          username: data.username,
          email: data.email,
          contactNumber: data.contactNumber,
        });
        setInitialValues({
          username: data.username,
          email: data.email,
          contactNumber: data.contactNumber,
        });
      } catch (error) {
        console.error('Failed to fetch personal details', error);
      }
    };
    
    fetchData();
  }, [userId]);

  const handleEditClick = () => setEditMode(true);

  const validateField = (name, value) => {
    let error = "";
    if (name === "username") {
      if (!value.trim()) {
        error = "Username cannot be empty.";
      } else if (value.length < 3) {
        error = "Username must be at least 3 characters long.";
      }
    } else if (name === "contactNumber") {
      if (!value.trim()) {
        error = "Contact number cannot be empty.";
      } else if (!/^\d*$/.test(value)) {
        error = "Contact number should contain only numbers.";
      } else if (value.length !== 10) {
        error = "Contact number must be 10 digits.";
      }
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "contactNumber" && (!/^\d*$/.test(value) || value.length > 10)) {
      return;
    }

    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSaveClick = async () => {
    const { username, contactNumber } = formValues;
    const usernameError = validateField("username", username);
    const contactNumberError = validateField("contactNumber", contactNumber);

    if (usernameError || contactNumberError) {
      setErrors({
        username: usernameError,
        contactNumber: contactNumberError,
      });
      return;
    }

    try {
      await editPersonalDetails({
        userId,
        payload: { username, contactNumber },
      });

      setInitialValues({ ...formValues });

      notification.success({
        message: 'Personal Details Update',
        description: 'Your details have been successfully updated',
      });

      setEditMode(false);
    } catch (error) {
      console.error('Failed to save personal details', error);
    }
  };

  const handleCancelClick = () => {
    setFormValues({ ...initialValues });
    setErrors({});
    setEditMode(false);
  };

  return (
    <Container maxWidth="lg" sx={{ position: "relative", right: "15px", mt: 4 }}>

    <Stack
      spacing={4}
      direction={{ xs: "column", md: "row" }}
      alignItems="flex-start"
    >
      {/* Left Side - Personal Details */}
      <Box sx={{ flex: 1, width: "100%" }}>
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ mb: 3, color: theme.palette.primary.main }}
        >
          Personal Details
        </Typography>

        <Stack spacing={3}>
          {["email", "username", "contactNumber"].map((key) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              {/* Label */}
              <Typography
                sx={{
                  flex: 0.4,
                  color: theme.palette.text.secondary,
                  fontSize: "1.4rem",
                  fontWeight: 500,
                }}
              >
                {key === "username"
                  ? "Name"
                  : key === "email"
                  ? "Email Id"
                  : "Contact No."}
              </Typography>

              {/* Value or Input */}
              {editMode && key !== "email" ? (
                <Stack sx={{ flex: 0.6 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    name={key}
                    value={formValues[key]}
                    onChange={handleInputChange}
                    error={!!errors[key]}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0.8rem",
                      },
                    }}
                  />
                  {errors[key] && <ErrorText>{errors[key]}</ErrorText>}
                </Stack>
              ) : (
                <Typography
                  sx={{
                    flex: 0.6,
                    color: theme.palette.text.primary,
                    fontSize: "1.4rem",
                  }}
                >
                  {formValues[key] || "N/A"}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>

        {/* Action Buttons */}
        {editMode ? (
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelClick}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveClick}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Save
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3, borderRadius: 2, px: 4 }}
            onClick={handleEditClick}
          >
            Edit Details
          </Button>
        )}
      </Box>

      {/* Right Side - Illustration */}
      <Box
        component="img"
        src={PersonalDetails}
        sx={{
          flex: 0.8,
          maxWidth: { xs: "100%", md: "38%" },
          borderRadius: 2,
          boxShadow: 2,
          alignSelf: { xs: "center", md: "flex-start" },
        }}
      />
    </Stack>
</Container>

  );
};

export default PersonalDetail;
